// Localisation + personalisation helpers for the Surveys 2.0 renderer.
import type {
  Localized,
  SurveyAnswerValue,
  SurveyBlock,
  SurveyDefinition,
} from "./surveyTypes";

/**
 * Pick the respondent's language from a Localized map: exact code (any case,
 * `-` or `_`), then the two-letter code, then a regional variant of it ("pt" →
 * "pt-br"), then the survey's default language, then English, then any (unless
 * `anyLanguage` is off: a label with a built-in default, e.g. Start, falls back
 * to that in the respondent's language rather than to a random translation).
 * A text the default language has but left empty (an optional description) is
 * empty, as in the editor, not filled from another language (#185684).
 * Plain strings are accepted for robustness.
 */
export const pickLocalized = (
  value: Localized | string | undefined | null,
  lang: string,
  defaultLanguage?: string,
  anyLanguage = true,
): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  const has = (code: string | undefined) =>
    !!code && typeof value[code] === "string" && value[code].length > 0;
  // Keys as the editor stores them ("pt-br") may differ in case or separator.
  const byNorm = new Map<string, string>();
  for (const key of Object.keys(value)) {
    if (has(key)) byNorm.set(key.toLowerCase().replace("_", "-"), key);
  }
  const norm = (lang || "").toLowerCase().replace("_", "-");
  const base = norm.substring(0, 2);
  const regional = Array.from(byNorm.keys()).find(
    (key) => key.startsWith(`${base}-`),
  );
  for (const code of [norm, base, regional]) {
    const key = code ? byNorm.get(code) : undefined;
    if (key) return value[key];
  }
  for (const code of [defaultLanguage, "en"]) {
    if (has(code)) return value[code as string];
  }
  const defaultNorm = (defaultLanguage || "").toLowerCase().replace("_", "-");
  const defaultIsEmpty = Object.keys(value).some(
    (key) =>
      key.toLowerCase().replace("_", "-") === defaultNorm &&
      typeof value[key] === "string",
  );
  if (!anyLanguage || defaultIsEmpty) return "";
  const first = Object.values(value).find(
    (text) => typeof text === "string" && text.length > 0,
  );
  return first ?? "";
};

export interface TokenContext {
  contact: Record<string, any>;
  fields: Record<string, any>;
  answers: Record<string, SurveyAnswerValue | undefined>;
  def?: SurveyDefinition;
  lang: string;
  yesLabel: string;
  noLabel: string;
}

/** Human-readable form of an answer (choice ids → labels). */
export const answerToText = (
  block: SurveyBlock | undefined,
  value: SurveyAnswerValue | undefined,
  ctx: Pick<TokenContext, "lang" | "def" | "yesLabel" | "noLabel">,
): string => {
  if (value === undefined || value === null) return "";
  const label = (id: string) => {
    const choice = block?.choices?.find((c) => c.id === id);
    return choice
      ? pickLocalized(choice.label, ctx.lang, ctx.def?.defaultLanguage)
      : id;
  };
  if (typeof value === "boolean") return value ? ctx.yesLabel : ctx.noLabel;
  if (Array.isArray(value)) {
    if (block?.type === "upload") return value.join(", ");
    return value.map(label).join(", ");
  }
  if (typeof value === "object") {
    const parts = (value.choices ?? []).map(label);
    if (value.other) parts.push(value.other);
    return parts.join(", ");
  }
  if (block?.type === "single") return label(String(value));
  return String(value);
};

/**
 * Replace `{{contact.first_name}}`, `{{field.plan}}`, `{{answer.nps_score}}`
 * (and bare `{{first_name}}` = contact). `{{contact.first_name|there}}` falls
 * back to "there" when the value is empty. Unknown tokens render empty.
 */
export const fillTokens = (text: string, ctx: TokenContext): string => {
  if (!text || text.indexOf("{{") < 0) return text;
  // An empty token without a fallback also drops the separator before it, so
  // "Glad to hear it, {{contact.first_name}}!" reads "Glad to hear it!" for anonymous visitors.
  return text.replace(/(,\s*|\s+)?\{\{\s*([^}|]+?)\s*(?:\|\s*([^}]*?)\s*)?\}\}/g, (_m, lead: string | undefined, path: string, fallback?: string) => {
    const [scope, ...rest] = path.split(".");
    let key = rest.join(".");
    let value: any;
    if (scope === "field" || scope === "fields") {
      value = ctx.fields?.[key];
    } else if (scope === "answer" || scope === "answers") {
      const block = ctx.def?.blocks.find((b) => b.key === key);
      value = answerToText(block, ctx.answers?.[key], ctx);
    } else {
      if (scope !== "contact" && scope !== "user") key = path;
      value = ctx.contact?.[key];
    }
    if (value === undefined || value === null || value === "") {
      return fallback !== undefined ? `${lead ?? ""}${fallback}` : "";
    }
    return `${lead ?? ""}${String(value)}`;
  });
};

/** Contact properties exposed to tokens and logic, from the session. */
export const contactFromSession = (session: any): Record<string, any> => {
  if (!session) return {};
  const contact: Record<string, any> = { ...session };
  delete contact.gleapHash;
  delete contact.fileAccessToken;
  const name: string = session.name ?? "";
  const firstName = name.trim().split(/\s+/)[0] ?? "";
  contact.first_name = contact.first_name ?? contact.firstName ?? firstName;
  contact.firstName = contact.firstName ?? contact.first_name;
  contact.last_name =
    contact.last_name ?? name.trim().split(/\s+/).slice(1).join(" ");
  contact.user_id = contact.user_id ?? session.userId;
  if (session.customData && typeof session.customData === "object") {
    for (const [k, v] of Object.entries(session.customData)) {
      if (contact[k] === undefined) contact[k] = v;
    }
  }
  return contact;
};
